import jwt_decode from 'jwt-decode'
import axios from 'axios'
import * as sessionModel from '@/services/sessionModel'

export default ({
    namespaced: true,

    state: {
        // Identidad del operador hidratada desde la respuesta de /gestion/canjear.
        // Se rehidrata desde sessionStorage para sobrevivir cambios de ruta y refresh.
        usuiden: sessionStorage.getItem('gestion_usuiden') || '',
        idArea: sessionStorage.getItem('gestion_idArea') || '',
        // Token opaco de sesion del operador. Vive en memoria (Vuex) para
        // sobrevivir el sessionStorage.clear() del logout del afiliado; se
        // persiste ademas en sessionStorage para sobrevivir un refresh.
        gestionSid: sessionStorage.getItem('gestion_sid') || '',
        // Estamos viendo un afiliado adentro del iframe?
        viendoAfiliado: sessionStorage.getItem('gestion_viendoAfiliado') === '1',
        nroafiActivo: Number(sessionStorage.getItem('gestion_nroafiActivo')) || 0,
        loading: false,
    },
    getters: {
        usuiden(state) {
            return state.usuiden
        },
        idArea(state) {
            return state.idArea
        },
        viendoAfiliado(state) {
            return state.viendoAfiliado
        },
        nroafiActivo(state) {
            return state.nroafiActivo
        },
        loading(state) {
            return state.loading
        },
    },
    mutations: {
        setOperadorIntermedio(state, data) {
            state.usuiden = (data && data.usuiden) || ''
            state.idArea = (data && data.idArea) || ''
            sessionStorage.setItem('gestion_usuiden', state.usuiden)
            sessionStorage.setItem('gestion_idArea', state.idArea)
        },
        setGestionSid(state, sid) {
            state.gestionSid = sid || ''
            if (state.gestionSid) {
                sessionStorage.setItem('gestion_sid', state.gestionSid)
            } else {
                sessionStorage.removeItem('gestion_sid')
            }
        },
        setLoading(state, payload) {
            state.loading = !!payload
        },
        setViendoAfiliado(state, { viendo, nroafi }) {
            state.viendoAfiliado = !!viendo
            state.nroafiActivo = viendo ? Number(nroafi) : 0
            if (viendo) {
                sessionStorage.setItem('gestion_viendoAfiliado', '1')
                sessionStorage.setItem('gestion_nroafiActivo', String(nroafi))
            } else {
                sessionStorage.removeItem('gestion_viendoAfiliado')
                sessionStorage.removeItem('gestion_nroafiActivo')
            }
        },
        limpiarOperador(state) {
            state.usuiden = ''
            state.idArea = ''
            state.gestionSid = ''
            state.viendoAfiliado = false
            state.nroafiActivo = 0
            // Toda la destruccion de storage pasa por sessionModel.
            sessionModel.clearOperatorSession()
        },
    },
    actions: {
        // En mounted de GestionPortal: canjea el codigo de un solo uso con el
        // back, recibe { usuiden, idArea, gestionSid } e hidrata el store.
        async canjearCodigo({ commit, dispatch }, code) {
            // Purga residuos de una sesion de afiliado previa en la misma
            // pestana (handoff limpia residuos). No toca gestion_*.
            sessionModel.clearAffiliateSession()
            try {
                const response = await axios({
                    method: 'get',
                    url: process.env.VUE_APP_API + 'gestion/canjear?code=' + encodeURIComponent(code),
                    withCredentials: false,
                    headers: { 'Content-Type': 'application/json' },
                })
                const data = (response.data && response.data.message) || {}
                commit('setOperadorIntermedio', { usuiden: data.usuiden, idArea: data.idArea })
                commit('setGestionSid', data.gestionSid)
            } catch (e) {
                dispatch('notificaciones/showSnack', {
                    message: 'No se pudo iniciar la sesion de gestion. Volvé a entrar desde el portal interno.',
                    type: '400',
                }, { root: true })
            }
        },

        // Cierra la vista del iframe y vuelve al form de busqueda. Limpia
        // la sesion del afiliado del sessionStorage. NO invalida la sesion de
        // operador en Redis: el gestionSid se mantiene en Vuex para que el
        // operador pueda buscar el siguiente afiliado sin reautenticarse.
        cerrarVistaAfiliado({ commit }) {
            // clearAffiliateSession nunca toca OPERATOR_KEYS (gestion_*): no
            // hace falta re-persistir nada, la sesion de operador queda intacta.
            sessionModel.clearAffiliateSession()
            commit('setViendoAfiliado', { viendo: false })
            commit('auth/RESET_STATE', null, { root: true })
        },

        // Cierra la sesion del operador en Redis (POST gestion/logout, best-effort).
        // ACTUALMENTE SIN CALLERS: por decision de producto se quito la salida
        // in-app; la sesion de gestion se drena por el TTL de 4h (o por SESSION-1
        // al reingresar desde SICA). Se conserva como capacidad por si se
        // necesita un cierre programatico en el futuro.
        cerrarSesionOperador({ commit, state }) {
            if (state.gestionSid) {
                axios({
                    method: 'post',
                    url: process.env.VUE_APP_API + 'gestion/logout',
                    withCredentials: false,
                    headers: { 'Content-Type': 'application/json' },
                    data: { gestionSid: state.gestionSid },
                }).catch(() => { /* best-effort: ignorar error de red o Redis caido */ })
            }
            // limpiarOperador delega toda la destruccion de storage en
            // sessionModel.clearOperatorSession().
            commit('limpiarOperador')
        },

        // Bind operador-afiliado: llama a loginAnillo con { nroafi, gestionSid },
        // recibe el JWT del afiliado y persiste la sesion. NO redirige: la
        // vista de gestionPortal renderiza el iframe que apunta al dashboard,
        // leyendo el token del sessionStorage compartido.
        async entrarComoAfiliado({ state, commit, dispatch }, nroafi) {
            // Guard doble-clic: si ya hay un canje en curso, ignorar el
            // segundo disparo (evita dos loginAnillo concurrentes y una
            // vista rota/duplicada).
            if (state.loading) return
            if (!state.gestionSid) {
                dispatch('notificaciones/showSnack', {
                    message: 'Sesion de gestion no inicializada. Volvé a entrar desde el portal interno.',
                    type: '400',
                }, { root: true })
                return
            }
            commit('setLoading', true)
            try {
                const response = await axios({
                    method: 'post',
                    url: process.env.VUE_APP_API + 'loginAnillo',
                    withCredentials: false,
                    headers: { 'Content-Type': 'application/json' },
                    data: { nroafi: parseInt(nroafi, 10), gestionSid: state.gestionSid },
                })
                const data = response.data || {}
                // Exito: el token viene anidado en .message. No-registrado:
                // loginAnillo responde el objeto plano (sin token) con error 2
                // y un msg explicativo (mismo contrato que obtenerAfiliado).
                const token = data.message && data.message['Authorization']
                if (!token) {
                    dispatch('notificaciones/showSnack', {
                        message: 'No se encontro un afiliado/a con esos datos, o no corresponde a tu delegacion.',
                        type: '400',
                    }, { root: true })
                    return
                }
                // Persistir la sesion del afiliado en sessionStorage. El
                // iframe que carga /afiliados/Dashboard arranca una nueva
                // instancia de la app y la lee desde ahi para autenticarse.
                sessionStorage.setItem('token', token)
                sessionStorage.setItem('nroafi', String(parseInt(nroafi, 10)))
                const decoded = jwt_decode(token)
                sessionStorage.setItem('caduca', String(decoded.exp))
                const subParts = (decoded.sub || '').split('*_*')
                if (subParts[1]) sessionStorage.setItem('emailAuth', subParts[1])
                if (decoded.mode === 'gestion') {
                    commit('auth/setEsModoGestion', true, { root: true })
                }
                commit('setViendoAfiliado', { viendo: true, nroafi: parseInt(nroafi, 10) })
            } catch (error) {
                console.log(error)
                if (error.response && error.response.status === 401) {
                    // TTL de operador (4h) vencido: loginAnillo rechaza la
                    // sesion de gestion. Cerramos la vista y volvemos a la
                    // busqueda; NUNCA al login de afiliados.
                    dispatch('cerrarVistaAfiliado')
                    dispatch('notificaciones/showSnack', {
                        message: 'Sesión de gestión vencida. Reingresá desde SICA.',
                        type: '400',
                    }, { root: true })
                } else {
                    dispatch('notificaciones/showSnack', {
                        message: 'Error al consultar el afiliado.',
                        type: '400',
                    }, { root: true })
                }
            } finally {
                commit('setLoading', false)
            }
        },
    },
})
